'use client';

import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { Chart } from './Chart';
import { ChartHamburgerMenu } from './combined/ChartHamburgerMenu';
import { ChartSkeleton } from './ChartSkeleton';
import { theme } from '../../config/theme';
import type { ChartData, ChartLayout, ChartConfig } from '../../types/chart';

const DoughnutChartContainer = styled.div<{ $isFullscreen: boolean }>`
  overflow: visible;
  transition: all 0.3s ease;
  background: transparent;
  
  ${({ $isFullscreen }) =>
    $isFullscreen &&
    `
    height: 100vh;
    width: 100vw;
    display: flex;
    flex-direction: column;
    padding: 40px;
    background: ${theme.colors.white};
  `}
`;

const ChartHeader = styled.div`
  padding: 16px 20px 8px 20px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-shrink: 0;
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const ChartTitle = styled.h3`
  display: block;
  margin: 0;
  font-size: 0.75rem;
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.gray[400]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ChartDescription = styled.p`
  display: block;
  margin: 4px 0 0 0;
  font-size: 0.875rem;
  color: ${theme.colors.gray[500]};
`;

const ChartContent = styled.div`
  padding: ${theme.spacing.lg};
  min-height: 400px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CenterInfo = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  pointer-events: none;
  z-index: 10;
`;

const CenterValue = styled.div`
  font-size: ${theme.fontSizes['2xl']};
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.primary[600]};
  line-height: 1;
`;

const CenterLabel = styled.div`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.gray[600]};
  margin-top: ${theme.spacing.xs};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export interface DoughnutChartData {
  labels: string[];
  values: number[];
}

export interface DoughnutChartProps {
  title: string;
  description?: string;
  data: DoughnutChartData;
  colors?: string[];
  centerLabel?: string;
  /** When true, the center shows only centerLabel and not the numeric total */
  hideCenterValue?: boolean;
  holeSize?: number;
  height?: number;
  loading?: boolean;
  className?: string;
}

export const DoughnutChart: React.FC<DoughnutChartProps> = ({
  title,
  description,
  data,
  colors,
  centerLabel = 'Total',
  hideCenterValue = false,
  holeSize = 0.6,
  height = 400,
  loading = false,
  className
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [zoomEnabled, setZoomEnabled] = useState(false);

  // Show skeleton if loading
  if (loading) {
    return <ChartSkeleton type="pie" height={`${height}px`} showLegend />;
  }

  const defaultColors = colors || [
    theme.colors.primary[500],
    theme.colors.status.success,
    theme.colors.status.warning,
    theme.colors.status.error,
    theme.colors.primary[300],
    theme.colors.chart.purple,
    theme.colors.chart.cyan,
    theme.colors.chart.lime,
  ];

  // Calculate total
  const total = data.values.reduce((sum, value) => sum + value, 0);

  const chartData: ChartData[] = [{
    type: 'pie',
    labels: data.labels,
    values: data.values,
    hole: holeSize,
    marker: {
      color: defaultColors,
      line: {
        color: theme.colors.white,
        width: 2,
      },
    },
    textinfo: 'none',
    hoverinfo: 'label+percent',
    hovertemplate: '<b>%{label}</b><br>Value: %{value}<br>Percentage: %{percent}<extra></extra>',
  }];

  const layout: ChartLayout = {
    height,
    hovermode: 'closest',
    margin: {
      l: 20,
      r: 20,
      t: 20,
      b: 20,
    },
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    font: {
      color: theme.colors.gray[700],
      family: 'Arial, sans-serif',
    },
    showlegend: true,
    legend: {
      layout: 'vertical',
      x: 1.05,
      y: 0.5,
      bgcolor: 'transparent',
    },
  };

  const config: ChartConfig = {
    displayModeBar: false,
    displaylogo: false,
    staticPlot: !zoomEnabled,
    responsive: true,
    scrollZoom: zoomEnabled,
    doubleClick: zoomEnabled ? 'reset' : false,
    modeBarButtonsToRemove: zoomEnabled 
      ? ['lasso2d', 'select2d', 'autoScale2d', 'resetScale2d']
      : ['lasso2d', 'select2d', 'pan2d', 'zoom2d', 'autoScale2d', 'resetScale2d'],
  };

  // Create table data for hamburger menu
  const tableData: Array<Record<string, string | number>> = data.labels.map((label, index) => {
    const value = data.values[index];
    const percentage = ((value / total) * 100).toFixed(1);
    return {
      label,
      value,
      percentage: `${percentage}%`
    };
  });

  return (
    <DoughnutChartContainer ref={chartRef} $isFullscreen={false} className={className}>
      <ChartHeader>
        <HeaderContent>
          <ChartTitle>{title}</ChartTitle>
          {description && <ChartDescription>{description}</ChartDescription>}
        </HeaderContent>
        <ChartHamburgerMenu 
          chartTitle={title}
          tableData={tableData}
          chartContainerRef={chartRef}
          onZoomToggle={setZoomEnabled}
          initialZoomEnabled={zoomEnabled}
          hideFullscreen
          hideTableView
        />
      </ChartHeader>
      
      <ChartContent>
            <Chart
              data={chartData}
              layout={layout}
              config={config}
              revision={zoomEnabled ? 1 : 0}
            />
        <CenterInfo>
          {!hideCenterValue && <CenterValue>{total.toLocaleString()}</CenterValue>}
          <CenterLabel>{centerLabel}</CenterLabel>
        </CenterInfo>
      </ChartContent>

    </DoughnutChartContainer>
  );
};